import { format } from 'date-fns'
import {
  convertPlatesToPolygons,
  convertPolygonsToWeldMap,
  ExternalUserFieldAccess,
  FormField,
  GeckoAssetModelType,
  GeckoComponentRect,
  GeckoHorizontalWeldJSON,
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoMeasurementUnit,
  GeckoPlateJSON,
  GeckoPolygonalPlateJSON,
  GeckoShipDeckBittJSON,
  GeckoShipDeckBulkheadJSON,
  GeckoShipDeckChockJSON,
  GeckoShipDeckCoamingJSON,
  GeckoShipDeckCompartmentJSON,
  GeckoShipDeckDoorJSON,
  GeckoShipDeckDrainJSON,
  GeckoShipDeckHatchJSON,
  GeckoShipDeckLadderJSON,
  GeckoShipDeckLockerJSON,
  GeckoVerticalWeldJSON,
  getFormFieldByDataKey,
  InputType,
  RolodexEntryJson,
  ShipDeckAssetModelJSON,
  ShipHullAssetModelJSON,
  WeldedPolygon,
  WrappingDirection,
} from 'types'
import { installationDateFormField } from 'types/assetModel/commonFields'
import { GeckoAssetModel } from 'types/entries/geckoAssetModel'
import { RolodexEntryPutJson } from 'types/rolodex/rolodexEntries'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  optionalStringSchema,
} from 'utils/zodHelpers'

import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from './assetModelEnums'
import { regenerateBasicShipHullComponent } from './assetModelHelpers'
import { BaseAssetModelData } from './baseAssetModelData'

export type GeckoShipAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.Ship
  installation_date: string | undefined
  components: Record<string, ShipDeckAssetModelJSON | ShipHullAssetModelJSON>
  effective_date: string
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
  ship_class: string | undefined
  vessel_name: string | undefined
  ship_class_number: number | undefined
  ship_status: string | undefined
  fleet: string | undefined
  date_status_changed: string | undefined
  homeport: string | undefined
  delivery_date: string | undefined
  commission_date: string | undefined
  uic: number | undefined
  frame_size: GeckoMeasurementJson | undefined
  berth: string | undefined
  force: string | undefined
  builder: string | undefined
  ship_id: string | undefined
  comments: string | undefined
  enlisted: number | undefined
  officers: number | undefined
  custodian: string | undefined
  keel_date: string | undefined
  award_date: string | undefined
  dead_weight: number | undefined
  draft_limit: number | undefined
  launch_date: string | undefined
  extreme_beam: number | undefined
  last_updated: string | undefined
  hull_material: string | undefined
  planning_yard: string | undefined
  stricken_date: string | undefined
  accommodations: string | undefined
  overall_length: number | undefined
  waterline_beam: number | undefined
  in_service_date: string | undefined
  no_of_waterjets: number | undefined
  propulsion_type: string | undefined
  no_of_propellers: number | undefined
  out_service_date: string | undefined
  ship_description: string | undefined
  waterline_length: number | undefined
  decommission_date: string | undefined
  full_displacement: number | undefined
  inactivation_date: string | undefined
  age_since_delivery: number | undefined
  light_displacement: number | undefined
  maintenance_category: string | undefined
  nuclear_planning_yard: string | undefined
  ships_program_manager: string | undefined
  max_navigational_draft: number | undefined
  age_since_launch_at_time_of_disposal: number | undefined
  years_from_commission_to_decommission: number | undefined
}

export const ShipAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.ShipDeck,
  GeckoComponentModelType.BasicShipHull,
]

export const NewShipAssetModelJSON = {
  name: 'New Asset Model Ship',
  type: GeckoAssetModelType.Ship,
  components: {},
  effective_date: format(Date.now(), 'yyyy-MM-dd'),
  installation_date: undefined,
  compass_orientation: undefined,
  plant_north_compass_orientation: undefined,
  ship_class: undefined,
  vessel_name: undefined,
  ship_class_number: undefined,
  ship_status: undefined,
  fleet: undefined,
  date_status_changed: undefined,
  homeport: undefined,
  delivery_date: undefined,
  commission_date: undefined,
  uic: undefined,
  frame_size: undefined,
  berth: undefined,
  force: undefined,
  builder: undefined,
  ship_id: undefined,
  comments: undefined,
  enlisted: undefined,
  officers: undefined,
  custodian: undefined,
  keel_date: undefined,
  award_date: undefined,
  dead_weight: undefined,
  draft_limit: undefined,
  launch_date: undefined,
  extreme_beam: undefined,
  last_updated: undefined,
  hull_material: undefined,
  planning_yard: undefined,
  stricken_date: undefined,
  accommodations: undefined,
  overall_length: undefined,
  waterline_beam: undefined,
  in_service_date: undefined,
  no_of_waterjets: undefined,
  propulsion_type: undefined,
  no_of_propellers: undefined,
  out_service_date: undefined,
  ship_description: undefined,
  waterline_length: undefined,
  decommission_date: undefined,
  full_displacement: undefined,
  inactivation_date: undefined,
  age_since_delivery: undefined,
  light_displacement: undefined,
  maintenance_category: undefined,
  nuclear_planning_yard: undefined,
  ships_program_manager: undefined,
  max_navigational_draft: undefined,
  age_since_launch_at_time_of_disposal: undefined,
  years_from_commission_to_decommission: undefined,
} as GeckoShipAssetModelJSON

export class GeckoShipAssetModel extends GeckoAssetModel<
  BaseAssetModelData & {
    ship_class: string | undefined
    vessel_name: string | undefined
    ship_class_number: number | undefined
    ship_status: string | undefined
    fleet: string | undefined
    date_status_changed: string | undefined
    homeport: string | undefined
    delivery_date: string | undefined
    commission_date: string | undefined
    uic: number | undefined
    frame_size: GeckoMeasurement | undefined
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(json)
    const recordData = json.data as Record<string, unknown>
    this.data.ship_class = recordData.ship_class as string | undefined
    this.data.vessel_name = recordData.vessel_name as string | undefined
    this.data.ship_class_number = recordData.ship_class_number as
      | number
      | undefined
    this.data.ship_status = recordData.ship_status as string | undefined
    this.data.fleet = recordData.fleet as string | undefined
    this.data.date_status_changed = recordData.date_status_changed as
      | string
      | undefined
    this.data.homeport = recordData.homeport as string | undefined
    this.data.delivery_date = recordData.delivery_date as string | undefined
    this.data.commission_date = recordData.commission_date as string | undefined
    this.data.uic = recordData.uic as number | undefined
    this.data.frame_size = recordData.frame_size
      ? GeckoMeasurement.CreateFromJSON(
          recordData.frame_size as GeckoMeasurementJson
        )
      : undefined
  }

  public static CreateFromJSON(json: RolodexEntryJson): GeckoShipAssetModel {
    return new GeckoShipAssetModel(json)
  }

  public asJSON(): GeckoShipAssetModelJSON {
    return generateShipAssetModel(
      this.data.name,
      this.raw.data.components as Record<
        string,
        ShipDeckAssetModelJSON | ShipHullAssetModelJSON
      >,
      this.data.effective_date,
      this.data.ship_class as string | undefined,
      this.data.vessel_name as string | undefined,
      this.data.ship_class_number as number | undefined,
      this.data.ship_status as string | undefined,
      this.data.fleet as string | undefined,
      this.data.date_status_changed as string | undefined,
      this.data.homeport as string | undefined,
      this.data.delivery_date as string | undefined,
      this.data.commission_date as string | undefined,
      this.data.uic as number | undefined,
      this.data.installation_date,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation,
      this.data.frame_size?.toJSON(),
      this.raw.data.berth as string | undefined,
      this.raw.data.force as string | undefined,
      this.raw.data.builder as string | undefined,
      this.raw.data.ship_id as string | undefined,
      this.raw.data.comments as string | undefined,
      this.raw.data.enlisted as number | undefined,
      this.raw.data.officers as number | undefined,
      this.raw.data.custodian as string | undefined,
      this.raw.data.keel_date as string | undefined,
      this.raw.data.award_date as string | undefined,
      this.raw.data.dead_weight as number | undefined,
      this.raw.data.draft_limit as number | undefined,
      this.raw.data.launch_date as string | undefined,
      this.raw.data.extreme_beam as number | undefined,
      this.raw.data.last_updated as string | undefined,
      this.raw.data.hull_material as string | undefined,
      this.raw.data.planning_yard as string | undefined,
      this.raw.data.stricken_date as string | undefined,
      this.raw.data.accommodations as string | undefined,
      this.raw.data.overall_length as number | undefined,
      this.raw.data.waterline_beam as number | undefined,
      this.raw.data.in_service_date as string | undefined,
      this.raw.data.no_of_waterjets as number | undefined,
      this.raw.data.propulsion_type as string | undefined,
      this.raw.data.no_of_propellers as number | undefined,
      this.raw.data.out_service_date as string | undefined,
      this.raw.data.ship_description as string | undefined,
      this.raw.data.waterline_length as number | undefined,
      this.raw.data.decommission_date as string | undefined,
      this.raw.data.full_displacement as number | undefined,
      this.raw.data.inactivation_date as string | undefined,
      this.raw.data.age_since_delivery as number | undefined,
      this.raw.data.light_displacement as number | undefined,
      this.raw.data.maintenance_category as string | undefined,
      this.raw.data.nuclear_planning_yard as string | undefined,
      this.raw.data.ships_program_manager as string | undefined,
      this.raw.data.max_navigational_draft as number | undefined,
      this.raw.data.age_since_launch_at_time_of_disposal as number | undefined,
      this.raw.data.years_from_commission_to_decommission as number | undefined
    )
  }
}

export function generateShipAssetModel(
  name: string,
  components: Record<string, ShipDeckAssetModelJSON | ShipHullAssetModelJSON>,
  effective_date: Date,
  ship_class: string | undefined,
  vessel_name: string | undefined,
  ship_class_number: number | undefined,
  ship_status: string | undefined,
  fleet: string | undefined,
  date_status_changed: string | undefined,
  homeport: string | undefined,
  delivery_date: string | undefined,
  commission_date: string | undefined,
  uic: number | undefined,
  installation_date: Date | undefined,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined,
  frame_size: GeckoMeasurementJson | undefined,
  berth: string | undefined = undefined,
  force: string | undefined = undefined,
  builder: string | undefined = undefined,
  ship_id: string | undefined = undefined,
  comments: string | undefined = undefined,
  enlisted: number | undefined = undefined,
  officers: number | undefined = undefined,
  custodian: string | undefined = undefined,
  keel_date: string | undefined = undefined,
  award_date: string | undefined = undefined,
  dead_weight: number | undefined = undefined,
  draft_limit: number | undefined = undefined,
  launch_date: string | undefined = undefined,
  extreme_beam: number | undefined = undefined,
  last_updated: string | undefined = undefined,
  hull_material: string | undefined = undefined,
  planning_yard: string | undefined = undefined,
  stricken_date: string | undefined = undefined,
  accommodations: string | undefined = undefined,
  overall_length: number | undefined = undefined,
  waterline_beam: number | undefined = undefined,
  in_service_date: string | undefined = undefined,
  no_of_waterjets: number | undefined = undefined,
  propulsion_type: string | undefined = undefined,
  no_of_propellers: number | undefined = undefined,
  out_service_date: string | undefined = undefined,
  ship_description: string | undefined = undefined,
  waterline_length: number | undefined = undefined,
  decommission_date: string | undefined = undefined,
  full_displacement: number | undefined = undefined,
  inactivation_date: string | undefined = undefined,
  age_since_delivery: number | undefined = undefined,
  light_displacement: number | undefined = undefined,
  maintenance_category: string | undefined = undefined,
  nuclear_planning_yard: string | undefined = undefined,
  ships_program_manager: string | undefined = undefined,
  max_navigational_draft: number | undefined = undefined,
  age_since_launch_at_time_of_disposal: number | undefined = undefined,
  years_from_commission_to_decommission: number | undefined = undefined
): GeckoShipAssetModelJSON {
  const shipEffectiveDate = effective_date.toISOString().split('T')[0]
  const shipInstallationDate = installation_date
    ? installation_date.toISOString().split('T')[0]
    : undefined

  return {
    name: name,
    type: GeckoAssetModelType.Ship,
    components: components,
    effective_date: shipEffectiveDate,
    installation_date: shipInstallationDate,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
    ship_class: ship_class,
    vessel_name: vessel_name,
    ship_class_number: ship_class_number,
    ship_status: ship_status,
    fleet: fleet,
    date_status_changed: date_status_changed,
    homeport: homeport,
    delivery_date: delivery_date,
    commission_date: commission_date,
    uic: uic,
    frame_size: frame_size,
    berth: berth,
    force: force,
    builder: builder,
    ship_id: ship_id,
    comments: comments,
    enlisted: enlisted,
    officers: officers,
    custodian: custodian,
    keel_date: keel_date,
    award_date: award_date,
    dead_weight: dead_weight,
    draft_limit: draft_limit,
    launch_date: launch_date,
    extreme_beam: extreme_beam,
    last_updated: last_updated,
    hull_material: hull_material,
    planning_yard: planning_yard,
    stricken_date: stricken_date,
    accommodations: accommodations,
    overall_length: overall_length,
    waterline_beam: waterline_beam,
    in_service_date: in_service_date,
    no_of_waterjets: no_of_waterjets,
    propulsion_type: propulsion_type,
    no_of_propellers: no_of_propellers,
    out_service_date: out_service_date,
    ship_description: ship_description,
    waterline_length: waterline_length,
    decommission_date: decommission_date,
    full_displacement: full_displacement,
    inactivation_date: inactivation_date,
    age_since_delivery: age_since_delivery,
    light_displacement: light_displacement,
    maintenance_category: maintenance_category,
    nuclear_planning_yard: nuclear_planning_yard,
    ships_program_manager: ships_program_manager,
    max_navigational_draft: max_navigational_draft,
    age_since_launch_at_time_of_disposal: age_since_launch_at_time_of_disposal,
    years_from_commission_to_decommission:
      years_from_commission_to_decommission,
  }
}

export const shipModelFormFields = (
  assetModelJSON: GeckoShipAssetModelJSON
): FormField[] => {
  const shipModelFormFields: FormField[] = [
    installationDateFormField(assetModelJSON.installation_date, true),
    {
      dataKey: 'ship_class',
      label: 'Ship Class',
      value: assetModelJSON.ship_class,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'vessel_name',
      label: 'Vessel Name',
      value: assetModelJSON.vessel_name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'ship_class_number',
      label: 'Ship Class Number',
      value: assetModelJSON.ship_class_number,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'ship_status',
      label: 'Ship Status',
      value: assetModelJSON.ship_status,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'fleet',
      label: 'Fleet',
      value: assetModelJSON.fleet,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'date_status_changed',
      label: 'Date Status Changed',
      value: assetModelJSON.date_status_changed,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'homeport',
      label: 'Homeport',
      value: assetModelJSON.homeport,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'delivery_date',
      label: 'Delivery Date',
      value: assetModelJSON.delivery_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'commission_date',
      label: 'Commission Date',
      value: assetModelJSON.commission_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
    {
      dataKey: 'uic',
      label: 'UIC',
      value: assetModelJSON.uic,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'frame_size',
      label: 'Frame Size',
      value: assetModelJSON.frame_size?.num,
      unit_magnitude: assetModelJSON.frame_size?.unit
        ? (GeckoMeasurementUnit.GetByID(assetModelJSON.frame_size?.unit) ??
          GeckoMeasurementUnit.None)
        : GeckoMeasurementUnit.None,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
  return shipModelFormFields
}

export const ShipJSONFromFormFields = (
  assetModelJSON: GeckoShipAssetModelJSON,
  formFields: FormField[]
): GeckoShipAssetModelJSON => {
  const shipClass = getFormFieldByDataKey(formFields, 'ship_class')?.value as
    | string
    | undefined
  const vesselName = getFormFieldByDataKey(formFields, 'vessel_name')?.value as
    | string
    | undefined
  const shipClassNumber = getFormFieldByDataKey(formFields, 'ship_class_number')
    ?.value as number | undefined
  const shipStatus = getFormFieldByDataKey(formFields, 'ship_status')?.value as
    | string
    | undefined
  const fleet = getFormFieldByDataKey(formFields, 'fleet')?.value as
    | string
    | undefined
  const dateStatusChanged = getFormFieldByDataKey(
    formFields,
    'date_status_changed'
  )?.value as string | undefined
  const homeport = getFormFieldByDataKey(formFields, 'homeport')?.value as
    | string
    | undefined
  const deliveryDate = getFormFieldByDataKey(formFields, 'delivery_date')
    ?.value as string | undefined
  const commissionDate = getFormFieldByDataKey(formFields, 'commission_date')
    ?.value as string | undefined
  const uic = getFormFieldByDataKey(formFields, 'uic')?.value as
    | number
    | undefined
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )?.value as string | undefined

  let frameSize = undefined
  const frameSizeFormField = getFormFieldByDataKey(formFields, 'frame_size')
  if (
    frameSizeFormField &&
    frameSizeFormField.unit_magnitude instanceof GeckoMeasurementUnit &&
    frameSizeFormField.unit_magnitude.id !== '' &&
    frameSizeFormField.value !== null
  ) {
    frameSize = {
      num: frameSizeFormField.value as number,
      unit: frameSizeFormField.unit_magnitude.id,
    }
  }

  return generateShipAssetModel(
    assetModelJSON.name,
    assetModelJSON.components,
    parseDate(assetModelJSON.effective_date) as Date,
    shipClass,
    vesselName,
    shipClassNumber,
    shipStatus,
    fleet,
    dateStatusChanged,
    homeport,
    deliveryDate,
    commissionDate,
    uic,
    parseDate(installationDate),
    assetModelJSON.compass_orientation,
    assetModelJSON.plant_north_compass_orientation,
    frameSize,
    assetModelJSON.berth,
    assetModelJSON.force,
    assetModelJSON.builder,
    assetModelJSON.ship_id,
    assetModelJSON.comments,
    assetModelJSON.enlisted,
    assetModelJSON.officers,
    assetModelJSON.custodian,
    assetModelJSON.keel_date,
    assetModelJSON.award_date,
    assetModelJSON.dead_weight,
    assetModelJSON.draft_limit,
    assetModelJSON.launch_date,
    assetModelJSON.extreme_beam,
    assetModelJSON.last_updated,
    assetModelJSON.hull_material,
    assetModelJSON.planning_yard,
    assetModelJSON.stricken_date,
    assetModelJSON.accommodations,
    assetModelJSON.overall_length,
    assetModelJSON.waterline_beam,
    assetModelJSON.in_service_date,
    assetModelJSON.no_of_waterjets,
    assetModelJSON.propulsion_type,
    assetModelJSON.no_of_propellers,
    assetModelJSON.out_service_date,
    assetModelJSON.ship_description,
    assetModelJSON.waterline_length,
    assetModelJSON.decommission_date,
    assetModelJSON.full_displacement,
    assetModelJSON.inactivation_date,
    assetModelJSON.age_since_delivery,
    assetModelJSON.light_displacement,
    assetModelJSON.maintenance_category,
    assetModelJSON.nuclear_planning_yard,
    assetModelJSON.ships_program_manager,
    assetModelJSON.max_navigational_draft,
    assetModelJSON.age_since_launch_at_time_of_disposal,
    assetModelJSON.years_from_commission_to_decommission
  )
}

export function regenerateShipAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw
  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }
  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.ShipDeck:
        regeneratedComponents[componentID] =
          regenerateShipDeckComponent(component)
        break
      case GeckoComponentModelType.BasicShipHull:
        regeneratedComponents[componentID] =
          regenerateBasicShipHullComponent(component)
        break
      default:
        throw new Error('Unable to determine this type of Ship Component!')
    }
  }
  assetModel.raw.data.components = regeneratedComponents
  return assetModel.raw
}

export function regenerateShipDeckComponent(
  shipDeck: Record<string, unknown>
): Record<string, unknown> {
  const shipFeatures = shipDeck.features as Record<
    string,
    | GeckoShipDeckCompartmentJSON
    | GeckoShipDeckBittJSON
    | GeckoShipDeckCoamingJSON
    | GeckoShipDeckChockJSON
    | GeckoShipDeckHatchJSON
    | GeckoShipDeckLockerJSON
    | GeckoShipDeckBulkheadJSON
    | GeckoShipDeckDoorJSON
    | GeckoShipDeckDrainJSON
    | GeckoShipDeckLadderJSON
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPolygonalPlateJSON
  >
  // If there's no features on the asset model, there's nothing to regenerate
  if (Object.keys(shipFeatures).length === 0) {
    return shipDeck
  }

  const shipDeckCompartments = new Map<string, GeckoShipDeckCompartmentJSON>()
  const shipDeckBitts = new Map<string, GeckoShipDeckBittJSON>()
  const shipDeckCoamings = new Map<string, GeckoShipDeckCoamingJSON>()
  const shipDeckChocks = new Map<string, GeckoShipDeckChockJSON>()
  const shipDeckHatches = new Map<string, GeckoShipDeckHatchJSON>()
  const shipDeckLockers = new Map<string, GeckoShipDeckLockerJSON>()
  const shipDeckBulkheads = new Map<string, GeckoShipDeckBulkheadJSON>()
  const shipDeckDoors = new Map<string, GeckoShipDeckDoorJSON>()
  const shipDeckDrains = new Map<string, GeckoShipDeckDrainJSON>()
  const shipDeckLadders = new Map<string, GeckoShipDeckLadderJSON>()
  const shipPlates = new Map<string, GeckoPlateJSON>()
  const shipPolygonalPlates = new Map<string, GeckoPolygonalPlateJSON>()

  for (const [featureID, feature] of Object.entries(shipFeatures)) {
    if (!feature.type) {
      console.error(
        `Unable to regenerate some features, as feature.type is missing!`
      )
    }
    switch (feature.type) {
      case GeckoFeatureModelType.ShipDeckCompartment:
        shipDeckCompartments.set(
          featureID,
          feature as GeckoShipDeckCompartmentJSON
        )
        break
      case GeckoFeatureModelType.ShipDeckBitt:
        shipDeckBitts.set(featureID, feature as GeckoShipDeckBittJSON)
        break
      case GeckoFeatureModelType.ShipDeckCoaming:
        shipDeckCoamings.set(featureID, feature as GeckoShipDeckCoamingJSON)
        break
      case GeckoFeatureModelType.ShipDeckChock:
        shipDeckChocks.set(featureID, feature as GeckoShipDeckChockJSON)
        break
      case GeckoFeatureModelType.ShipDeckHatch:
        shipDeckHatches.set(featureID, feature as GeckoShipDeckHatchJSON)
        break
      case GeckoFeatureModelType.ShipDeckLocker:
        shipDeckLockers.set(featureID, feature as GeckoShipDeckLockerJSON)
        break
      case GeckoFeatureModelType.ShipDeckBulkhead:
        shipDeckBulkheads.set(featureID, feature as GeckoShipDeckBulkheadJSON)
        break
      case GeckoFeatureModelType.ShipDeckDoor:
        shipDeckDoors.set(featureID, feature as GeckoShipDeckDoorJSON)
        break
      case GeckoFeatureModelType.ShipDeckDrain:
        shipDeckDrains.set(featureID, feature as GeckoShipDeckDrainJSON)
        break
      case GeckoFeatureModelType.ShipDeckLadder:
        shipDeckLadders.set(featureID, feature as GeckoShipDeckLadderJSON)
        break
      case GeckoFeatureModelType.Plate:
        shipPlates.set(featureID, feature as GeckoPlateJSON)
        break
      case GeckoFeatureModelType.PolygonalPlate:
        shipPolygonalPlates.set(featureID, feature as GeckoPolygonalPlateJSON)
        break
      case GeckoFeatureModelType.VerticalWeld:
      case GeckoFeatureModelType.HorizontalWeld:
        continue

      default:
        throw new Error('Unable to determine this type of Ship Feature!')
    }
  }

  const componentBoundsJSON = shipDeck.component_bounds as Record<
    string,
    number
  >

  const componentBounds = GeckoComponentRect.CreateFromXYs(
    shipDeck.id as string,
    componentBoundsJSON.x_start as number,
    componentBoundsJSON.y_start as number,
    componentBoundsJSON.x_end as number,
    componentBoundsJSON.y_end as number,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const wrappingDirection = WrappingDirection.None
  const polygonArray: Array<WeldedPolygon> = []

  convertPlatesToPolygons(
    [...shipPlates.values()],
    componentBounds,
    wrappingDirection,
    polygonArray
  )

  const welds = convertPolygonsToWeldMap(
    polygonArray,
    componentBounds,
    wrappingDirection
  )

  const features = new Map<
    string,
    | GeckoVerticalWeldJSON
    | GeckoShipDeckCompartmentJSON
    | GeckoShipDeckBittJSON
    | GeckoShipDeckCoamingJSON
    | GeckoShipDeckChockJSON
    | GeckoShipDeckHatchJSON
    | GeckoShipDeckLockerJSON
    | GeckoShipDeckBulkheadJSON
    | GeckoShipDeckDoorJSON
    | GeckoShipDeckDrainJSON
    | GeckoShipDeckLadderJSON
    | GeckoPlateJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPolygonalPlateJSON
  >([
    ...shipPlates.entries(),
    ...welds.entries(),
    ...shipDeckCompartments.entries(),
    ...shipDeckBitts.entries(),
    ...shipDeckCoamings.entries(),
    ...shipDeckChocks.entries(),
    ...shipDeckHatches.entries(),
    ...shipDeckLockers.entries(),
    ...shipDeckBulkheads.entries(),
    ...shipDeckDoors.entries(),
    ...shipDeckDrains.entries(),
    ...shipDeckLadders.entries(),
    ...shipPolygonalPlates.entries(),
  ])

  shipDeck.features = Object.fromEntries(features) as Record<string, unknown>

  return shipDeck
}
